import {
    IAuthResult,
    LoginExpert,
    ILoginExpert,
    IConfirmAccount,
    ConfirmAccount,
    ExpertConfirmationData,
    SendVerificationCode,
    VerificationCodeSendingStatus,
    CheckVerificationCode,
    CheckAccountConfirmationToken,
    ISendConfirmationEmailAgain,
    SendConfirmationEmailAgain,
    IConfirmEmailByToken,
    ConfirmEmailByToken,
    IChangePassword,
    ChangePassword,
    IResetPassword,
    ResetPassword,
    IUpdatePhone,
    UpdatePhone,
    ISendVerificationCodeForUpdatePhone,
    SendVerificationCodeForUpdatePhone,
    ISendVerificationCode,
    ICheckVerificationCode,
} from '../../api/ApiClient';
import { INotification } from 'components';
import { clearAuthToken, setAuthToken, setHeaderTitle, clearHeaderTitle } from '_helpers';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';
import { setUserAuthTokens, clearUserAuthTokens } from '../../helpers/UserAuthTokens';
import { ExpertStatusNames } from 'components/enums';

const initialState: IState = {
    auth: null,
    expertConfirmationData: null,
    verificationCodeSendingStatus: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setAuth(state, auth: IAuthResult): IState {
            if (auth && auth.token) {
                setAuthToken(auth.token);
            }

            return { ...state, auth, isAsc: !!auth.profile.attestationSubCommissionId };
        },
        setExpertConfirmationData(state, expertConfirmationData: ExpertConfirmationData): IState {
            return { ...state, expertConfirmationData };
        },
        setVerificationCodeSendingStatus(state, verificationCodeSendingStatus: VerificationCodeSendingStatus): IState {
            return { ...state, verificationCodeSendingStatus };
        },
        logout(): IState {
            clearAuthToken();
            clearHeaderTitle();
            clearUserAuthTokens();

            return initialState;
        },
    },
    effects: (dispatch: any) => ({
        async login(data: ILoginExpert) {
            const auth = await apiConnector.execApi(dispatch, apiClient => apiClient.login(new LoginExpert(data)));

            if (!auth) return;

            setAuthToken(auth.token);
            setHeaderTitle(auth.userAuthTokens ? auth.userAuthTokens[0].name : '');

            if (auth.userAuthTokens) {
                setUserAuthTokens(auth.userAuthTokens);
            } else {
                clearUserAuthTokens();
            }

            this.setAuth(auth);
        },
        async fetchProfile() {
            const profile = await apiConnector.execApi(dispatch, apiClient => apiClient.profile());
            if (profile && profile.status) {
                document.title = `${ExpertStatusNames[profile.status]} | АСА НМП`;
            }
            this.setAuth({ profile });
        },
        async checkAccountConfirmationTokenAsync(data: ICheckConfirmationToken) {
            const expertConfirmationData = await apiConnector.execApi(dispatch, apiClient => apiClient.checkAccountConfirmationToken(new CheckAccountConfirmationToken(data)));

            this.setExpertConfirmationData(expertConfirmationData);
        },
        async sendRegistrationVerificationCodeAsync(data: ISendVerificationCode) {
            const verificationCodeSendingStatus = await apiConnector.execApi(dispatch, apiClient => apiClient.sendRegistrationVerificationCode(new SendVerificationCode(data)));

            this.setVerificationCodeSendingStatus(verificationCodeSendingStatus);
            return verificationCodeSendingStatus;
        },
        async checkRegistrationVerificationCodeAsync(data: ICheckRegisterVerifyCode) {
            return apiConnector.execApi(dispatch, apiClient => apiClient.checkRegistrationVerificationCode(new CheckVerificationCode(data)));
        },
        async confirmAccountAsync(data: IConfirmAccount) {
            const auth = await apiConnector.execApi(dispatch, apiClient => apiClient.confirmAccount(new ConfirmAccount(data)));

            if (!auth) return;

            this.setAuth(auth);
        },
        async sendConfirmationEmailAsync(body: ISendConfirmationEmailAgain) {
            const res = await apiConnector.execApi(dispatch, apiClient => apiClient.sendConfirmationEmailAgain(new SendConfirmationEmailAgain(body)));
            if (res) {
                dispatch.notifications.addNotification([{ message: 'Ссылка для подтверждения электронной почты успешно отправлена!', appearance: 'success' }] as INotification[]);
            }
            return res;
        },
        async confirmEmailByTokenAsync(body: IConfirmEmailByToken) {
            const res = await apiConnector.execApi(dispatch, apiClient => apiClient.confirmEmailByToken(new ConfirmEmailByToken(body)));
            if (res) {
                dispatch.notifications.addNotification([{ message: 'Email успешно подтвержден!', appearance: 'success' }] as INotification[]);
            }
            return res;
        },
        async changePasswordAsync(body: IChangePassword) {
            const res = await apiConnector.execApi(dispatch, apiClient => apiClient.changePassword(new ChangePassword(body)));
            if (res) {
                dispatch.notifications.addNotification([{ message: 'Пароль успешно изменен!', appearance: 'success' }] as INotification[]);
            }
            return res;
        },
        async sendResetPasswordVerificationCodeAsync(data: ISendVerificationCode) {
            return apiConnector.execApi(dispatch, apiClient => apiClient.sendResetPasswordVerificationCode(new SendVerificationCode(data)));
        },
        async checkResetPasswordVerificationCodeAsync(data: ICheckVerificationCode) {
            return apiConnector.execApi(dispatch, apiClient => apiClient.checkResetPasswordVerificationCode(new CheckVerificationCode(data)));
        },
        async resetPasswordAsync(data: IResetPassword) {
            const auth = await apiConnector.execApi(dispatch, apiClient => apiClient.resetPassword(new ResetPassword(data)));

            if (auth) {
                this.setAuth(auth);
            }
            return auth;
        },
        async sendUpdatePhoneVerificationCodeAsync(body: ISendVerificationCodeForUpdatePhone) {
            return apiConnector.execApi(dispatch, apiClient => apiClient.sendUpdatePhoneVerificationCode(new SendVerificationCodeForUpdatePhone(body)));
        },
        async checkUpdatePhoneVerificationCodeAsync(data: ICheckVerificationCode) {
            return apiConnector.execApi(dispatch, apiClient => apiClient.checkUpdatePhoneVerificationCode(new CheckVerificationCode(data)));
        },
        async updatePhoneAsync(body: IUpdatePhone) {
            const profile = await apiConnector.execApi(dispatch, apiClient => apiClient.updatePhone(new UpdatePhone(body)));

            if (profile) {
                this.setAuth({ profile });
                dispatch.notifications.addNotification([{ message: 'Телефон успешно обновлен!', appearance: 'success' }] as INotification[]);
            }
        },
    }),
});

interface ICheckConfirmationToken {
    token: string;
    captcha?: string;
}

interface IState {
    auth: IAuthResult | null;
    isAsc?: boolean;
    expertConfirmationData: ExpertConfirmationData | null;
    verificationCodeSendingStatus: VerificationCodeSendingStatus | null;
}

interface ICheckRegisterVerifyCode {
    phone: string;
    verificationCode: string;
    captcha?: string;
}
